/**
 * Closeout learning digest (Epic #207, Phase C) — the admin glance.
 *
 * Shows what the office's document edits taught the form mapper: the source fixes
 * the "why" engine auto-applied to templates (each with a one-tap Undo), the
 * suggestions it captured but didn't auto-apply (checkbox binds, ambiguous
 * matches, edits by non-admins), and the fields that keep needing the same fix.
 *
 * Admin-only, collapsed by default, fetches only when opened — a quiet report,
 * not a wall in the way of clearing the queue.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RotateCcw, Sparkles } from "lucide-react";
import { api } from "../lib/api";

interface AppliedLearning {
    id: string;
    fieldLabel: string | null;
    templateName: string | null;
    beforeValue: string | null;
    afterValue: string | null;
    sourcePath: string | null;
    createdAt: string;
}
interface LearningSuggestion {
    templateFieldId: string;
    fieldLabel: string | null;
    templateName: string | null;
    kind: string;
    sourcePath: string | null;
    staticValue: string | null;
    support: number;
    lastAt: string;
}
interface RecurringField {
    templateFieldId: string;
    fieldLabel: string | null;
    templateName: string | null;
    edits: number;
    lastAt: string;
}
interface Digest {
    applied: AppliedLearning[];
    suggestions: LearningSuggestion[];
    recurring: RecurringField[];
}

export function CloseoutLearningDigest({ className }: { className?: string } = {}) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);
    const digestQuery = useQuery({
        queryKey: ["close-out-learning-digest"],
        queryFn: () => api.get<Digest>("/api/close-out/learning/digest"),
        enabled: open,
    });
    const revert = useMutation({
        mutationFn: (eventId: string) =>
            api.post<{ ok: boolean; reason?: string }>(`/api/close-out/learning/events/${eventId}/revert`, {}),
        onSuccess: (r) => {
            if (r?.ok === false) {
                toast.error(r.reason ?? "Couldn't undo that.");
                return;
            }
            toast.success("Undone — the field's mapping is back to what it was.");
            qc.invalidateQueries({ queryKey: ["close-out-learning-digest"] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? "Couldn't undo that."),
    });

    const d = digestQuery.data;
    const nothing = d && d.applied.length === 0 && d.suggestions.length === 0 && d.recurring.length === 0;

    return (
        <div
            className={`min-w-[220px] flex-1 rounded-xl border border-emerald-300/70 bg-card p-3 dark:border-emerald-800/60 lg:flex-none${
                className ? ` ${className}` : ""
            }`}
        >
            <div className="mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1 text-[10.5px] font-extrabold uppercase tracking-[.07em] text-emerald-700 dark:text-emerald-300">
                    <Sparkles className="h-3 w-3" /> What edits taught the mapper
                </span>
                <button
                    className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300"
                    onClick={() => setOpen((o) => !o)}
                >
                    {open ? "Close" : "Open"}
                </button>
            </div>
            {!open && (
                <p className="text-[11px] leading-snug text-foreground/55">
                    When you fix a field on a document, the system figures out why and — for a clear source fix —
                    teaches the template so future forms fill right. Open to see what it learned. Corbin-only.
                </p>
            )}
            {open && digestQuery.isLoading && (
                <p className="flex items-center gap-1 text-[11px] text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                </p>
            )}
            {open && !digestQuery.isLoading && nothing && (
                <p className="text-[11px] leading-snug text-foreground/55">
                    Nothing learned yet — fix a field in <b>Edit document</b> and the reason shows up here.
                </p>
            )}
            {open && d && !nothing && (
                <div className="flex flex-col gap-3">
                    {d.applied.length > 0 && (
                        <div>
                            <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[.06em] text-foreground/55">
                                Auto-applied to the template
                            </div>
                            <div className="flex flex-col gap-1">
                                {d.applied.map((a) => (
                                    <div
                                        key={a.id}
                                        className="rounded-lg border border-emerald-300/60 bg-emerald-50/40 px-2 py-1.5 text-[11px] dark:border-emerald-800/50 dark:bg-emerald-950/20"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span className="truncate font-medium" title={a.fieldLabel ?? ""}>
                                                {a.fieldLabel ?? "field"}
                                            </span>
                                            <span className="truncate text-muted-foreground">
                                                {a.templateName ?? ""}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={revert.isPending}
                                                onClick={() => revert.mutate(a.id)}
                                                title="Undo — restore the prior mapping"
                                                className="ml-auto flex flex-none items-center gap-0.5 rounded bg-emerald-700 px-1.5 py-0.5 text-[10px] font-semibold text-white disabled:opacity-50"
                                            >
                                                <RotateCcw className="h-2.5 w-2.5" /> Undo
                                            </button>
                                        </div>
                                        <div className="mt-0.5 text-foreground/60">
                                            now pulls from <b>{a.sourcePath}</b>
                                            {a.afterValue ? <> — e.g. “{a.afterValue}”</> : null}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {d.suggestions.length > 0 && (
                        <div>
                            <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[.06em] text-foreground/55">
                                Suggested (not auto-applied)
                            </div>
                            <div className="flex flex-col gap-1">
                                {d.suggestions.map((s) => (
                                    <div
                                        key={`${s.templateFieldId}-${s.sourcePath ?? ""}`}
                                        className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-[11px]"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <span className="truncate font-medium">{s.fieldLabel ?? "field"}</span>
                                            {s.support > 1 && (
                                                <span className="flex-none rounded bg-amber-100 px-1 py-px text-[9px] font-bold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                                                    ×{s.support}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-0.5 text-foreground/60">
                                            {s.kind === "checkbox_bind" ? (
                                                <>
                                                    bind to <b>{s.staticValue}</b> — use <b>Fix future forms</b> to
                                                    apply
                                                </>
                                            ) : (
                                                <>
                                                    maybe map to <b>{s.sourcePath}</b>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {d.recurring.length > 0 && (
                        <div>
                            <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[.06em] text-foreground/55">
                                Keeps needing fixing
                            </div>
                            <div className="flex flex-col gap-1">
                                {d.recurring.map((r) => (
                                    <div
                                        key={r.templateFieldId}
                                        className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/20 px-2 py-1 text-[11px]"
                                    >
                                        <span className="truncate font-medium">{r.fieldLabel ?? "field"}</span>
                                        <span className="truncate text-muted-foreground">{r.templateName ?? ""}</span>
                                        <span className="ml-auto flex-none rounded bg-rose-100 px-1 py-px text-[9px] font-bold text-rose-700 dark:bg-rose-900/50 dark:text-rose-300">
                                            {r.edits} edits
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
